import React, { useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiClock,
  FiMail,
  FiMessageCircle,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiUser,
} from "react-icons/fi";


import {
  Col,
  Row,
  Card,
  CardHeader,
  Button,
  Spinner,

} from "reactstrap";
import api from "../../utills/api";
import BreadCrumb from "../../Components/Common/BreadCrumb";

const Conversations = () => {
  const [leads, setLeads] = useState([]);
  const [activities, setActivities] = useState([]);

  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState("All");

  const [refreshing, setRefreshing] =
      useState(false);

  const loadConversations = async () => {
    try {
      setLoading(true);

      const leadsResponse = await api.get("/leads");

      const leadsData = leadsResponse?.data?.data;

      const leadList = Array.isArray(leadsData)
        ? leadsData
        : Array.isArray(leadsData?.leads)
        ? leadsData.leads
        : Array.isArray(leadsData?.items)
        ? leadsData.items
        : [];

      setLeads(leadList);

      /*
       * Lead activities are loaded individually because
       * the current backend exposes activities through
       * the lead detail response.
       */
      const activityResults = await Promise.all(
        leadList.slice(0, 100).map(async (lead) => {
          const leadId = lead?._id || lead?.id;

          if (!leadId) return [];

          try {
            const response = await api.get(
              `/leads/${leadId}`
            );

            const data = response?.data?.data;

            const leadActivities = Array.isArray(
              data?.activities
            )
              ? data.activities
              : [];

            return leadActivities.map((activity) => ({
              ...activity,
              leadId,
              lead,
            }));
          } catch (error) {
            console.error(
              `Failed to load activities for lead ${leadId}`,
              error
            );

            return [];
          }
        })
      );

      setActivities(activityResults.flat());
    } catch (error) {
      console.error(
        "Failed to load conversations:",
        error
      );

      setLeads([]);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConversations();
  }, []);

  const getLeadName = (lead) =>
    lead?.name ||
    `${lead?.firstName || ""} ${
      lead?.lastName || ""
    }`.trim() ||
    "Unknown Lead";

  const getActivityType = (activity) =>
    String(activity?.type || "Other");

  const getChannel = (activity) => {
    const type = getActivityType(activity).toLowerCase();

    if (type.includes("whatsapp")) return "WhatsApp";
    if (type.includes("called") || type.includes("call"))
      return "Call";
    if (type.includes("email")) return "Email";

    return "Other";
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case "WhatsApp":
        return <FiMessageCircle />;

      case "Call":
        return <FiPhone />;

      case "Email":
        return <FiMail />;

      default:
        return <FiClock />;
    }
  };

  const formatDate = (date) => {
    if (!date) return "—";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return "—";
    }

    return parsed.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredActivities = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return activities
      .filter((activity) => {
        const lead = activity?.lead;

        const leadName =
          getLeadName(lead).toLowerCase();

        const description = String(
          activity?.description || ""
        ).toLowerCase();

        const channel = getChannel(activity);

        const matchesSearch =
          !keyword ||
          leadName.includes(keyword) ||
          description.includes(keyword);

        const matchesChannel =
          channelFilter === "All" ||
          channel === channelFilter;

        return matchesSearch && matchesChannel;
      })
      .sort(
        (a, b) =>
          new Date(b?.createdAt || 0) -
          new Date(a?.createdAt || 0)
      );
  }, [activities, search, channelFilter]);

  const selectedActivities = useMemo(() => {
    if (!selectedLead) return [];

    const selectedId =
      selectedLead?._id || selectedLead?.id;

    return activities
      .filter(
        (activity) =>
          String(activity?.leadId) ===
          String(selectedId)
      )
      .sort(
        (a, b) =>
          new Date(a?.createdAt || 0) -
          new Date(b?.createdAt || 0)
      );
  }, [activities, selectedLead]);

  const totalConversations = activities.length;

  const whatsappCount = activities.filter(
    (activity) =>
      getChannel(activity) === "WhatsApp"
  ).length;

  const callCount = activities.filter(
    (activity) =>
      getChannel(activity) === "Call"
  ).length;

  const emailCount = activities.filter(
    (activity) =>
      getChannel(activity) === "Email"
  ).length;

    document.title =
    "Conversation | eduLeads.Online";

  return (
    <div className="page-content">
      <div className="container-fluid">
         <BreadCrumb
          title="Conversation"
          pageTitle="Communication"
        />

        {/* Header */}

         <Row>
                  <Col lg={12}>
                    <Card>
                      <CardHeader>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                          <div className="flex-grow-1">
                            <h5 className="mb-0">
                              Conversations
                            </h5>
        
                            <small className="text-muted">
                              View communication history across your leads.
                            </small>
                          </div>
    
                        </div>
                      </CardHeader>
                    </Card>
                  </Col>
                </Row>

        {/* Summary */}
        <div className="row">

          <div className="col-xl-3 col-md-6">
            <div className="card card-animate">
              <div className="card-body">
                <div className="d-flex align-items-center">

                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                      <FiMessageCircle />
                    </span>
                  </div>

                  <div className="flex-grow-1 ms-3">
                    <p className="text-uppercase fw-medium text-muted mb-1">
                      Total Conversations
                    </p>

                    <h4 className="mb-0">
                      {totalConversations}
                    </h4>
                  </div>

                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-animate">
              <div className="card-body">
                <div className="d-flex align-items-center">

                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-success-subtle text-success rounded fs-3">
                      <FiMessageCircle />
                    </span>
                  </div>

                  <div className="flex-grow-1 ms-3">
                    <p className="text-uppercase fw-medium text-muted mb-1">
                      WhatsApp
                    </p>

                    <h4 className="mb-0">
                      {whatsappCount}
                    </h4>
                  </div>

                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-animate">
              <div className="card-body">
                <div className="d-flex align-items-center">

                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-info-subtle text-info rounded fs-3">
                      <FiPhone />
                    </span>
                  </div>

                  <div className="flex-grow-1 ms-3">
                    <p className="text-uppercase fw-medium text-muted mb-1">
                      Calls
                    </p>

                    <h4 className="mb-0">
                      {callCount}
                    </h4>
                  </div>

                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-animate">
              <div className="card-body">
                <div className="d-flex align-items-center">

                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                      <FiMail />
                    </span>
                  </div>

                  <div className="flex-grow-1 ms-3">
                    <p className="text-uppercase fw-medium text-muted mb-1">
                      Emails
                    </p>

                    <h4 className="mb-0">
                      {emailCount}
                    </h4>
                  </div>

                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Conversations */}
        <div className="row">

          {/* Conversation List */}
          <div className="col-xl-5">

            <div className="card">

              <div className="card-header">
                <div className="row g-2">

                  <div className="col-md-12">
                    <div className="search-box">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search conversations..."
                        value={search}
                        onChange={(e) =>
                          setSearch(e.target.value)
                        }
                      />

                      <FiSearch className="search-icon" />
                    </div>
                  </div>

                  <div className="col-md-12">
                    <select
                      className="form-select"
                      value={channelFilter}
                      onChange={(e) =>
                        setChannelFilter(
                          e.target.value
                        )
                      }
                    >
                      <option value="All">
                        All Channels
                      </option>

                      <option value="WhatsApp">
                        WhatsApp
                      </option>

                      <option value="Call">
                        Calls
                      </option>

                      <option value="Email">
                        Email
                      </option>

                      <option value="Other">
                        Other
                      </option>
                    </select>
                  </div>

                </div>
              </div>

              <div
                className="card-body p-0"
                style={{
                  maxHeight: "650px",
                  overflowY: "auto",
                }}
              >

                {loading ? (
                  <div className="text-center py-5">
                    <div
                      className="spinner-border text-primary"
                      role="status"
                    >
                      <span className="visually-hidden">
                        Loading...
                      </span>
                    </div>
                  </div>
                ) : filteredActivities.length === 0 ? (
                  <div className="text-center py-5 px-3">

                    <div className="avatar-lg mx-auto mb-3">
                      <div className="avatar-title bg-light text-muted rounded-circle fs-1">
                        <FiMessageCircle />
                      </div>
                    </div>

                    <h5>No conversations found</h5>

                    <p className="text-muted mb-0">
                      Communication activity will appear
                      here when leads are contacted.
                    </p>

                  </div>
                ) : (
                  filteredActivities.map(
                    (activity, index) => {
                      const lead =
                        activity?.lead;

                      const channel =
                        getChannel(activity);

                      const leadId =
                        lead?._id || lead?.id;

                      const selectedId =
                        selectedLead?._id ||
                        selectedLead?.id;

                      const isSelected =
                        String(leadId) ===
                        String(selectedId);

                      return (
                        <button
                          type="button"
                          key={
                            activity?._id ||
                            activity?.id ||
                            index
                          }
                          className={`w-100 text-start border-0 border-bottom bg-transparent ${
                            isSelected
                              ? "bg-light"
                              : ""
                          }`}
                          onClick={() =>
                            setSelectedLead(lead)
                          }
                        >
                          <div className="p-3">

                            <div className="d-flex">

                              <div className="avatar-sm flex-shrink-0">
                                <span className="avatar-title rounded-circle bg-primary-subtle text-primary">
                                  {getLeadName(
                                    lead
                                  )
                                    .charAt(0)
                                    .toUpperCase()}
                                </span>
                              </div>

                              <div className="flex-grow-1 ms-3">

                                <div className="d-flex justify-content-between">

                                  <h6 className="mb-1">
                                    {getLeadName(
                                      lead
                                    )}
                                  </h6>

                                  <small className="text-muted">
                                    {formatDate(
                                      activity?.createdAt
                                    )}
                                  </small>

                                </div>

                                <div className="d-flex align-items-center gap-2 mb-1">

                                  <span className="text-primary">
                                    {getChannelIcon(
                                      channel
                                    )}
                                  </span>

                                  <span className="text-muted small">
                                    {channel}
                                  </span>

                                </div>

                                <p className="text-muted mb-0 text-truncate">
                                  {activity?.description ||
                                    "No conversation details"}
                                </p>

                              </div>

                            </div>

                          </div>
                        </button>
                      );
                    }
                  )
                )}

              </div>
            </div>
          </div>

          {/* Conversation Detail */}
          <div className="col-xl-7">

            <div className="card">

              {!selectedLead ? (
                <div className="card-body text-center py-5">

                  <div className="avatar-lg mx-auto mb-3">
                    <div className="avatar-title bg-light text-muted rounded-circle fs-1">
                      <FiUser />
                    </div>
                  </div>

                  <h5>Select a conversation</h5>

                  <p className="text-muted mb-0">
                    Select a conversation from the list to
                    view the complete communication history.
                  </p>

                </div>
              ) : (
                <>
                  {/* Lead Header */}
                  <div className="card-header">
                    <div className="d-flex align-items-center">

                      <div className="avatar-md flex-shrink-0">
                        <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-4">
                          {getLeadName(
                            selectedLead
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                      </div>

                      <div className="flex-grow-1 ms-3">

                        <h5 className="mb-1">
                          {getLeadName(
                            selectedLead
                          )}
                        </h5>

                        <p className="text-muted mb-0">

                          {selectedLead?.phone ||
                            selectedLead?.mobile ||
                            "No phone"}

                          {selectedLead?.email &&
                            ` • ${selectedLead.email}`}

                        </p>

                      </div>

                      <div>
                        <span className="badge bg-primary-subtle text-primary">
                          {selectedActivities.length}{" "}
                          Activities
                        </span>
                      </div>

                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="card-body">

                    {selectedActivities.length === 0 ? (
                      <div className="text-center py-5">

                        <FiClock
                          size={32}
                          className="text-muted mb-3"
                        />

                        <h6>
                          No communication history
                        </h6>

                        <p className="text-muted mb-0">
                          No activities have been recorded
                          for this lead yet.
                        </p>

                      </div>
                    ) : (
                      <div className="conversation-timeline">

                        {selectedActivities.map(
                          (activity, index) => {
                            const channel =
                              getChannel(activity);

                            return (
                              <div
                                className="d-flex mb-4"
                                key={
                                  activity?._id ||
                                  activity?.id ||
                                  index
                                }
                              >

                                <div className="avatar-sm flex-shrink-0">
                                  <span className="avatar-title rounded-circle bg-light text-primary">
                                    {getChannelIcon(
                                      channel
                                    )}
                                  </span>
                                </div>

                                <div className="flex-grow-1 ms-3">

                                  <div className="d-flex justify-content-between align-items-start">

                                    <div>
                                      <h6 className="mb-1">
                                        {getActivityType(
                                          activity
                                        )}
                                      </h6>

                                      <span className="badge bg-light text-muted">
                                        {channel}
                                      </span>
                                    </div>

                                    <small className="text-muted">
                                      {formatDate(
                                        activity?.createdAt
                                      )}
                                    </small>

                                  </div>

                                  <div className="mt-2 p-3 bg-light rounded">
                                    {activity?.description ||
                                      "No description available."}
                                  </div>

                                  {activity?.userId && (
                                    <small className="text-muted d-block mt-2">
                                      Recorded by user
                                    </small>
                                  )}

                                </div>

                              </div>
                            );
                          }
                        )}

                      </div>
                    )}

                  </div>
                </>
              )}

            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default Conversations;